import React, { useEffect, useMemo } from "react";
import { CloseCircleOutlined, SearchOutlined } from "@ant-design/icons";
import type { SchemaListItem, ToolPartProps } from "./types";

interface FindSchemasInput {
    query: string;
}

interface FindSchemasMatch {
    schemaId: string;
    name: string;
    description?: string;
    fieldCount: number;
    dataSetCount?: number;
    state?: string;
    createdDate?: string;
    updatedDate?: string;
    createdByEmail?: string;
    updatedByEmail?: string;
    totalRecordCount?: number;
}

interface FindSchemasOutput {
    matches: FindSchemasMatch[];
    total: number;
}

function resolveFieldCount(s: SchemaListItem): number {
    if (typeof s?.fieldsCount === "number") return s.fieldsCount;
    return Array.isArray(s?.fields) ? s.fields.length : 0;
}

export default function FindSchemasTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<FindSchemasInput, FindSchemasOutput>) {
    const { schemaList, addToolOutput } = helpers;

    const result = useMemo<FindSchemasOutput>(() => {
        const q = (input?.query || "").trim().toLowerCase();
        const filtered = (schemaList || []).filter((s) => {
            if (!q) return true;
            return (s.name || "").toLowerCase().includes(q) || (s.description || "").toLowerCase().includes(q);
        });
        return {
            matches: filtered.slice(0, 25).map((s) => ({
                schemaId: s.schemaId,
                name: s.name,
                description: s.description,
                fieldCount: resolveFieldCount(s),
                dataSetCount: typeof s.dataSetCount === "number" ? s.dataSetCount : undefined,
                state: s.state,
                createdDate: s.createdDate,
                updatedDate: s.updatedDate,
                createdByEmail: s.createdByEmail,
                updatedByEmail: s.updatedByEmail,
                totalRecordCount: typeof s.totalRecordCount === "number" ? s.totalRecordCount : undefined,
            })),
            total: filtered.length,
        };
    }, [input?.query, schemaList]);

    useEffect(() => {
        if (state === "input-available") {
            addToolOutput({ tool: "findSchemas", toolCallId, output: result });
        }
    }, [state, result, toolCallId, addToolOutput]);

    const matches = state === "output-available" && output ? output.matches : result.matches;
    const total = state === "output-available" && output ? output.total : result.total;

    return (
        <div className="rounded-2xl border border-zinc-200 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-[#001D3D]">
                <SearchOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">
                    Search results{input?.query ? ` for "${input.query}"` : ""}
                </span>
            </div>
            {matches.length === 0 ? (
                <div className="text-xs text-zinc-500">No schemas matched.</div>
            ) : (
                <ul className="space-y-1 text-xs">
                    {matches.map((m) => (
                        <li key={m.schemaId} className="rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1">
                            <div className="font-medium text-zinc-900">{m.name}</div>
                            <div className="text-[10px] text-zinc-500">
                                {m.fieldCount} fields
                                {typeof m.dataSetCount === "number" ? ` · ${m.dataSetCount} data sets` : ""}
                                {m.state ? ` · ${m.state}` : ""} · id: {m.schemaId}
                            </div>
                            {m.description && <div className="text-[10px] text-zinc-600">{m.description}</div>}
                        </li>
                    ))}
                </ul>
            )}
            {total > matches.length && <div className="mt-1 text-[10px] text-zinc-500">Showing {matches.length} of {total} matches.</div>}
            {state === "output-error" && (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}
        </div>
    );
}
